<template>
  <CDialog v-model="isOpen" class="dialog--large dialog--is-scrollable">
    <header class="dialog-header align-items-center">
      <div class="flex-fill">
        <h1 class="h2 mrgv0">
          {{ $t('exploitationcenter.title') }}
        </h1>
      </div>

      <Btn variant="icon" @click="isOpen = !isOpen">
        <Icon id="close" :scale="0.75" />
      </Btn>
    </header>

    <div class="dialog-body">
      <header class="dialog-subheader">
        <span class="mrgr">
          {{ $t('exploitationcenter.subtitle') }}
        </span>
        <Btn
          type="button"
          variant="primary"
          @click="
            addProfileAndReorder(
              $t('exploitationcenter.profile.new') + ' ' + (profiles.length + 1)
            )
          "
        >
          <icon id="add" />
          {{ $t('exploitationcenter.add') }}
        </Btn>
      </header>
      <section class="mrgt">
        <ul class="ce-container pdg0">
          <li key="instruction" class="ce-profile-container pdgb">
            <aside class="ce-timeline-box first" />
            <span class="info-chip">
              <Icon id="phone-up" :scale="0.75" />
              Incoming calls
            </span>
          </li>
          <li
            v-for="(item, index) in profilesWithDefault"
            :key="item.name"
            class="ce-profile-container"
            :class="{ hint: item.isHint, pdgv: !item.isHint }"
          >
            <aside
              class="ce-timeline-box"
              :class="{ active: item.is_active && !item.isHint }"
            />
            <div class="profile-container">
              <ProfileDropZone
                v-if="index === 0 && !item.isHint"
                :class="{ 'mrgb+': isDragging }"
                :is-dragging="isDragging"
                :zone-index="item.order"
                @onDrop="onDrop"
              >
              </ProfileDropZone>
              <div v-if="item.isHint" class="info-chip">
                {{ item.text }}
              </div>
              <ExploitationCenterProfile
                v-else
                class="flex-fill"
                :profile="item"
                :available-users="getUsersByRole('operator')"
                :available-lines="getLines"
                :available-regions="getRegions"
                :is-dragging="isDragging"
                @edit="e => editProfile({ item, profile: e })"
                @delete="() => deleteProfileAndReorder(item)"
                @startDrag="startDrag()"
                @stopDrag="stopDrag"
              />
              <ProfileDropZone
                v-if="index < profilesWithDefault.length - 2 && !item.isHint"
                :class="{ 'mrgt+': isDragging }"
                :is-dragging="isDragging"
                :zone-index="item.order + 1"
                @onDrop="onDrop"
              ></ProfileDropZone>
            </div>
          </li>
        </ul>
      </section>
    </div>
  </CDialog>
</template>

<script>
import eventBus from '@/eventBus';
import ExploitationCenterProfile from '@/components/exploitationcenter/Profile';
import { setRoutingProfiles } from '@/services/routing-profiles';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import ProfileDropZone from '@/components/exploitationcenter/ProfileDropZone';

export default {
  components: {
    ProfileDropZone,
    ExploitationCenterProfile,
  },

  data() {
    return {
      isDragging: false,
      isOpen: false,
      hasLoaded: false,
    };
  },

  computed: {
    ...mapState('routingProfiles', ['profiles']),
    ...mapState('auth', ['user']),
    ...mapGetters('users', ['getUsersByRole']),
    ...mapGetters('courses', ['getLines', 'getRegions']),
    profilesWithDefault() {
      const profilesWithHints = [...this.profiles].sort((a, b) =>
        a.order > b.order ? 1 : -1
      );
      if (profilesWithHints.length >= 2) {
        profilesWithHints.splice(1, 0, {
          name: 'hint1',
          text: this.$t('exploitationcenter.hint1', {
            profile1: profilesWithHints[0].name,
            profile2: profilesWithHints[1]?.name,
          }),
          isHint: true,
        });
      }
      return profilesWithHints.concat([
        {
          name: 'hint2',
          text: this.$t('exploitationcenter.hint2'),
          isHint: true,
        },
        {
          name: this.$t('exploitationcenter.profile.default-title'),
          special: this.$t('exploitationcenter.profile.default-text'),
          is_active: true,
          disableEdit: true,
        },
      ]);
    },
  },

  watch: {
    isOpen() {
      this.hasLoaded = false;
      if (this.isOpen) {
        this.getProfiles();
      }
    },
    profiles: {
      deep: true,
      async handler() {
        if (this.user.can_access_routing_profiles && this.hasLoaded) {
          const processedProfiles = this.profiles.map(profile => {
            const processed = { ...profile };
            processed.target_user = profile.target_user?.[0] ?? null; //ToDo: update when API changes
            return processed;
          });
          await setRoutingProfiles(processedProfiles);
        } else if (this.user.can_access_routing_profiles) {
          this.hasLoaded = true;
        }
      },
    },
  },

  mounted() {
    eventBus.$on('toggleExploitationCenter', this.handleToggle);
    this.$store.dispatch('courses/getCourses');
  },

  beforeDestroy() {
    eventBus.$off('toggleExploitationCenter', this.handleToggle);
  },

  methods: {
    handleToggle() {
      this.isOpen = !this.isOpen;
    },
    ...mapActions('routingProfiles', [
      'getProfiles',
      'addProfile',
      'editProfile',
      'deleteProfile',
    ]),
    ...mapMutations('routingProfiles', ['setProfiles']),
    addProfileAndReorder(name) {
      this.pushAllProfiles();
      this.addProfile(name);
    },
    deleteProfileAndReorder(item) {
      this.deleteProfile(item);
      let profilesCopy = JSON.parse(JSON.stringify(this.profiles));
      profilesCopy
        .sort((a, b) => (a.order > b.order ? 1 : -1))
        .forEach((profile, index) => (profile.order = index));
      this.setProfiles(profilesCopy);
    },
    startDrag() {
      this.isDragging = true;
    },
    onDrop(zoneIndex, profileOrder) {
      this.isDragging = false;
      this.moveProfile(zoneIndex, profileOrder);
    },
    pushAllProfiles() {
      let profilesCopy = JSON.parse(JSON.stringify(this.profiles));
      profilesCopy.forEach(profile => (profile.order = profile.order + 1));
      this.setProfiles(profilesCopy);
    },
    moveProfile(zoneIndex, profileOrderToMove) {
      let profilesCopy = JSON.parse(JSON.stringify(this.profiles));

      let profileMoved = profilesCopy.find(
        profile => profile.order === profileOrderToMove
      );

      if (zoneIndex > profileOrderToMove) {
        profilesCopy
          .filter(
            profile =>
              profile.order < zoneIndex && profile.order > profileOrderToMove
          )
          .forEach(profile => (profile.order = profile.order - 1));
        profileMoved.order = zoneIndex - 1;
      } else {
        profilesCopy
          .filter(
            profile =>
              profile.order >= zoneIndex && profile.order < profileOrderToMove
          )
          .forEach(profile => (profile.order = profile.order + 1));
        profileMoved.order = zoneIndex;
      }

      this.setProfiles(profilesCopy);
    },
    stopDrag(order) {
      this.isDragging = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.info-chip {
  margin: 2px 3px 2px 0;
  padding: 2px 12px 2px 10px;

  line-height: 24px;

  background-color: $brand-alt-50;
  border-radius: 24px;
}

.dialog-subheader {
  display: flex;
  align-items: center;
  font-size: 1.25rem;
}

.dialog-body {
  > section {
    display: flex;
    position: relative;

    > .ce-timeline {
      width: 3rem;
      position: relative;

      &:after {
        content: '';
        position: absolute;
        left: 50%;
        border: 1px dashed $brand-500;
        height: 100%;
        transform: translateX(-50%);
      }
    }

    > .ce-container {
      width: 100%;
      counter-reset: section;
      list-style: none;

      > .ce-profile-container {
        position: relative;
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        flex: 1;

        &.hint {
          align-items: center;

          &:after {
            content: '';
            position: absolute;
            left: 1.5rem;
            width: 1.5rem;
            border: 1px dashed $brand-500;
          }
        }

        &:before {
          content: '';
          position: absolute;
          left: 1.5rem;
          border-left: 2px solid $brand-500;
          height: 100%;
          transform: translateX(-50%);
          top: 0;
        }

        &:first-of-type {
          margin-bottom: -1rem;

          &:before {
            top: 2.5rem;
            border-left-style: dashed;
          }
        }

        &:nth-of-type(2):before {
          top: 2.5rem;
        }

        &:last-of-type {
          &:before {
            height: 2rem;
          }

          > .ce-timeline-box {
            &:after {
              content: '';
              border: 0;
              background-color: $brand-500;
              height: 1rem;
              clip-path: polygon(
                0 45%,
                65% 45%,
                65% 0,
                100% 50%,
                65% 100%,
                65% 55%,
                0 55%
              );
            }
          }
        }

        > .ce-timeline-box {
          position: relative;
          height: 3rem;
          flex: 0 0 3rem;

          &.active:before {
            content: counter(section);
            counter-increment: section;
            width: 2rem;
            height: 2rem;
            display: flex;
            justify-content: center;
            align-items: center;
            background-color: $brand-500;
            border-radius: 50%;
            color: white;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }

          &.first:before {
            content: '↓';
            width: 2.5rem;
            height: 2.5rem;
            display: flex;
            justify-content: center;
            align-items: center;
            background-color: $brand-alt-50;
            border-radius: 50%;
            position: absolute;
            top: 50%;
            left: 50%;
            font-size: 1.5rem;
            transform: translate(-50%, -50%);
          }

          &.active:after {
            content: '';
            position: absolute;
            border: 1px solid $brand-500;
            right: 0;
            width: 1rem;
            top: 50%;
            transform: translateY(-50%);
          }
        }
      }
    }
  }
}

.profile-container {
  flex: 1;
  display: flex;
  flex-direction: column;
}
</style>
